import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import shallowCompare from 'react-addons-shallow-compare';
import styled from 'foundations-theming/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { FileTypes } from 'file-manager-components/constants';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import PickersContext from 'ContentComponents/context/PickersContext';
import FileBase from 'ContentComponents/components/cmv2/components/FileBase';
import VideoPreviewContainer from 'ContentComponents/components/cmv2/media/VideoPreviewContainer';
import ContentRoutes from 'ContentUtils/Routes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.videoPicker.${key}`;
const DEFAULT_ASPECT_RATIO = 16 / 9;
const PreviewIframeWrapper = styled.div.withConfig({
  displayName: "VideoBase__PreviewIframeWrapper",
  componentId: "sc-1sv4x6b-0"
})(({
  aspectRatio
}) => {
  return `
      position: relative;
      width: 100%;
      height: 0;
      padding-bottom: ${aspectRatio * 100}%;
  `;
});
const PreviewIframe = styled.iframe.withConfig({
  displayName: "VideoBase__PreviewIframe",
  componentId: "sc-1sv4x6b-1"
})(({
  disabled
}) => {
  return `
  background-color: black;
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  ${disabled ? 'pointer-events: none;' : ''}
  `;
});
class VideoBase extends Component {
  constructor(props) {
    super(props);
    this.handleVideoPickerOpen = () => {
      this.setState({
        videoPickerOpen: true
      });
    };
    this.handleVideoPickerClose = () => {
      this.setState({
        videoPickerOpen: false
      });
    };
    this.handleVideoSelect = video => {
      this.handleVideoPickerClose();
      this.props.handleVideoSelect(video);
    };
    this.onClearVideo = this.onClearVideo.bind(this);
    this.setIframeDisabled = this.setIframeDisabled.bind(this);
    this.setIframeEnabled = this.setIframeEnabled.bind(this);
    this.state = {
      iframeDisabled: false,
      videoPickerOpen: false
    };
  }
  componentDidMount() {
    document.addEventListener('mousedown', this.setIframeDisabled);
    document.addEventListener('mouseup', this.setIframeEnabled);
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.setIframeDisabled);
    document.removeEventListener('mouseup', this.setIframeEnabled);
  }
  onClearVideo() {
    const {
      onFieldChange
    } = this.props;
    onFieldChange({
      player_id: null
    });
  }
  setIframeDisabled() {
    this.setState({
      iframeDisabled: true
    });
  }
  setIframeEnabled() {
    this.setState({
      iframeDisabled: false
    });
  }
  renderHubSpotPreview() {
    const {
      videoData,
      value,
      posterUrl
    } = this.props;
    const {
      iframeDisabled
    } = this.state;
    if (!videoData || !value) {
      return null;
    }
    const {
      conversion_asset,
      player_id
    } = value || {};
    const {
      height,
      width,
      name
    } = videoData || {};
    const aspectRatio = height && width ? height / width : DEFAULT_ASPECT_RATIO;
    const extraParams = {
      conversion_asset: conversion_asset ? JSON.stringify(conversion_asset) : '',
      playerType: 'editorPreview',
      posterUrl,
      renderContext: 'video-field-preview'
    };
    const iframeSrc = ContentRoutes.getVideo2PreviewUrl(player_id, extraParams);
    return /*#__PURE__*/_jsx(PreviewIframeWrapper, {
      aspectRatio: aspectRatio,
      children: /*#__PURE__*/_jsx(PreviewIframe, {
        "data-test-id": "video-preview",
        disabled: iframeDisabled,
        allowFullScreen: true,
        frameBorder: "0",
        height: height,
        src: iframeSrc,
        width: width,
        title: name
      })
    });
  }
  renderPicker() {
    if (!this.state.videoPickerOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx(PickersContext.Consumer, {
      children: ({
        HubSpotVideoPicker
      }) => {
        if (!HubSpotVideoPicker) {
          throw new Error('Missing required HubSpotVideoPicker PickerComponent');
        }
        return /*#__PURE__*/_jsx(HubSpotVideoPicker, {
          onClose: this.handleVideoPickerClose,
          onSelect: this.handleVideoSelect
        });
      }
    });
  }
  renderContent(hasValue) {
    const {
      isLoading,
      showPreview
    } = this.props;
    if (isLoading) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        layout: "centered",
        minHeight: 100
      });
    } else if (!showPreview || !hasValue) {
      return this.renderPicker();
    }
    return /*#__PURE__*/_jsxs(VideoPreviewContainer, {
      children: [this.renderHubSpotPreview(), this.renderPicker()]
    });
  }
  render() {
    const {
      fieldKey,
      handleVideoSelect,
      hasHubSpotVideoAccess,
      helpText,
      inlineHelpText,
      label,
      readOnly,
      required,
      useOnDark,
      value,
      videoData
    } = this.props;
    const hasValue = !!(value && videoData);
    return /*#__PURE__*/_jsx(FileBase, {
      browseText: unescapedText(i18nKey('buttons.browseVideos')),
      fieldKey: fieldKey,
      fileName: videoData && videoData.title,
      fileType: FileTypes.MOVIE,
      inlineHelpText: inlineHelpText,
      handleFileSelect: handleVideoSelect,
      hasValue: hasValue,
      helpText: helpText,
      label: label,
      onClearFile: this.onClearVideo,
      onFileManagerOpen: this.handleVideoPickerOpen,
      readOnly: readOnly,
      removeOnly: !hasHubSpotVideoAccess,
      required: required,
      uploadText: unescapedText(i18nKey('buttons.uploadVideo')),
      useOnDark: useOnDark,
      children: this.renderContent(hasValue)
    });
  }
}
VideoBase.defaultProps = {
  hasHubSpotVideoAccess: true,
  label: '',
  handleVideoSelect: emptyFunction,
  showPreview: true,
  value: {}
};
export default VideoBase;